<script setup lang="ts">
import { inject } from 'vue'
import type { PropType } from 'vue'
import type { NavItem } from '../UiNavigation.types'
import type { INavButton } from './UiNav.types'
import { useRouteLink } from '@/composables/useRouteLink'

const { routeLink } = useRouteLink()

defineProps({
  navigation: {
    type: Object as PropType<NavItem[]>,
    required: true,
  },
})

const location = useRoute()

const hideLang = inject('hideLang')

const navButtons: INavButton[] | undefined = inject('navButtons')
const openElementId = ref('')

function toggle(name: string) {
  if (openElementId.value === name) {
    openElementId.value = ''
  } else {
    openElementId.value = name
  }
}

function enterKeyHandler(event: KeyboardEvent, name: string) {
  toggle(name)
  nextTick(() => {
    const firstChild = (event.target as HTMLElement)?.nextElementSibling
      ?.firstChild
    if (firstChild && openElementId) {
      ;(firstChild as HTMLElement)?.focus()
    }
  })
}

function showLang() {
  return !location.fullPath.includes('/o-benefit-systems/kariera') && !hideLang
}

</script>

<template>
  <nav class="navigation-desktop">
    <ul class="navigation-desktop__list">
      <li
        v-for="(itemFirst, index) in navigation"
        :key="itemFirst.link"
        class="navigation-desktop__list_item--first"
        tabindex="0"
        @keyup.enter="enterKeyHandler($event, `firstIndex-${index}`)"
        @mouseenter="toggle(`firstIndex-${index}`)"
        @mouseleave="toggle(`firstIndex-${index}`)"
      >
        <span
          v-if="itemFirst.children?.length"
          :class="[
            'navigation-desktop__list_item-link',
            'navigation-desktop__list_item-dropdown',
            { 'is-active' : openElementId === `firstIndex-${index}` }
          ]"
        >
          {{ $t(itemFirst.label) }}
        </span>
        
        <NuxtLink
          v-else-if="$t(itemFirst.link) && !itemFirst.external"
          :class="[ 'navigation-desktop__list_item-link', 'navigation-desktop__list_item-link--noDropdown' ]"
          :to="routeLink($t(itemFirst.link), undefined, itemFirst.linkToLocale)"
        >
          {{ $t(itemFirst.label) }}
        </NuxtLink>

        <a
          v-else
          :class="[
            'navigation-desktop__list_item-link',
            'navigation-desktop__list_item-link--noDropdown',
          ]"
          :href="$t(itemFirst.link)"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t(itemFirst.label) }}
        </a>

        <transition name="fade">
          <ul
            v-if="itemFirst.children?.length"
            v-show="openElementId === `firstIndex-${index}`"
            class="navigation-desktop__inner"
          >
            <li
              v-for="itemSecond in itemFirst.children"
              :key="itemSecond.label"
              class="navigation-desktop__list_item--second"
            >
              <NuxtLink
                v-if="$t(itemSecond.link) && !itemSecond.external"
                class="navigation-desktop__list_item-link"
                :to="routeLink($t(itemSecond.link), undefined, itemSecond.linkToLocale)"
                >
                  {{ $t(itemSecond.label) }}
              </NuxtLink>

              <a
                v-else
                class="navigation-desktop__list_item-link"
                :href="$t(itemSecond.link)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ $t(itemSecond.label) }}
              </a>
            </li>
          </ul>
        </transition>
      </li>
    </ul>

    <div class="navigation-desktop__buttons">
      <UiButton
        v-for="(navButton, index) in navButtons"
        :key="index"
        :size="navButton.size"
        :to="navButton.to"
        @click="navButton.callback"
      >
        {{ $t(navButton.label) }}
      </UiButton>
      
      <UiLanguage v-if="showLang()" />
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.navigation-desktop {
  $root: &;

  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-left: 2rem;
  background-color: transparent;
  transition: all .5s ease-in-out;

  &__list {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 2rem;

    &_item {

      &--first {
        position: relative;
        #{$root}__list_item-link {
          color: $text-400;
          font-weight: 400;
          font-size: 1.6rem;
          line-height: 2.4rem;
          white-space: nowrap;

          &#{$root}__list_item-dropdown {
            &:after {
              display: inline-block;
              width: 1.6rem;
              height: 1.6rem;
              margin-left: 0.4rem;
              vertical-align: middle;
              background-image: url('/icons/arrow_down.svg');
              background-position: center;
              background-size: cover;
              content: '';
            }
          }
        }

        &:hover {
          & > #{$root}__list_item-link {
            color: $purple-400;

            &#{$root}__list_item-dropdown {
              &:after {
                background-image: url('/icons/arrow_down_purple.svg');
              }
            }
          }
        }
      }

      &--second {
        margin: 0.8rem 1.6rem 0;
        padding: 0.8rem 0;
        background-color: $white;
        border-bottom: 1px solid $gray-200;
        #{$root}__list_item-link {
          font-weight: 500;
          font-size: 2rem;
          line-height: 2.8rem;
        }

        &:hover {
          margin: 0.8rem 0 0 0;
          padding: 0.8rem 1.6rem;
          background-color: $gray-200;
          border-radius: 0.8rem;
        }
      }
    }
  }

  &__inner {
    position: absolute;
    top: 5rem;
    left: -4rem;
    z-index: 9;
    min-width: 32rem;
    padding: 3.2rem 1.6rem;
    background-color: $white;
    border: 0.1rem solid $gray-200;
    border-radius: 1.2rem;

    &:after {
      position: absolute;
      top: 1.3rem;
      left: 4rem;
      z-index: 3;
      display: inline-block;
      width: 4.7rem;
      height: 4.7rem;
      background-color: $white;
      border-radius: 0.6rem;
      transform: rotate(45deg) translate(-50%, -50%);
      content: '';
    }
  }

  &__buttons {
    display: flex;
    justify-content: space-between;
    gap: 0.8rem;
  }

  @include atL {
    margin-left: 3.8rem;

    #{$root}__list {
      gap: 3.2rem;
    }
  }
}
</style>
